/**
 * @description Schedule level logs shown as a tab (similar to the Logs tab in the search page).
 * Shows schedule-level changes (schedule / route / pickup / drop where trip_id is null).
 *
 * A single user action (e.g. editing stops) writes many temporal rows that all share the
 * same timestamp + agent, so the raw list is very noisy. To keep it readable we group those
 * rows into ONE line per operation (same date+time+agent). Clicking "Details" on a line opens
 * a single popup that merges every change in that operation, each labelled by a human friendly
 * identifier (route origin -> destination, pickup/drop location).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Modal, Typography, Descriptions, Tag, message, Row, Col, Divider, Select } from 'antd';
import moment from 'moment';
import { getScheduleUpdateLogsById } from '../../actions/schedule.actions';
import { isValidArray, isValid, isValidArrayWithValues } from '../../utils/utilities';
import { loadingAction } from '../../actions/loading-actions';
import { useDispatch } from 'react-redux';

const { Text } = Typography;
const { Option } = Select;

// Maps each log source to a singular label + the field that holds the entity id.
const LOG_SOURCES = [
    { key: 'schedules', type: 'Schedule', idField: 'schedule_id' },
    { key: 'routes', type: 'Route', idField: 'route_id' },
    { key: 'pickups', type: 'Pickup', idField: 'pickup_id' },
    { key: 'drops', type: 'Drop', idField: 'drop_id' }
];

const SUMMARY_ORDER = ['Schedule', 'Route', 'Pickup', 'Drop'];

const formatFieldName = (fieldName) => {
    if (!isValid(fieldName)) {
        return '';
    }
    return String(fieldName)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    return String(value);
};

const formatDateTime = (value) => isValid(value) ? moment(value).format('DD MMM YYYY, HH:mm:ss') : '-';

// These columns hold a time-of-day only. The DB returns them as a full datetime where the
// date part is meaningless, so display just the clock time. Read as UTC so the raw time
// (e.g. 17:00) is preserved instead of being timezone-shifted.
const TIME_FIELDS = ['start_time', 'end_time', 'timing'];

const formatTimeValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }
    const asUtc = moment.utc(value, moment.ISO_8601, true);
    if (asUtc.isValid()) {
        return asUtc.format('hh:mm A');
    }
    const asTime = moment(value, ['HH:mm:ss', 'HH:mm'], true);
    if (asTime.isValid()) {
        return asTime.format('hh:mm A');
    }
    return String(value);
};

// Builds a human friendly label for a change using the identifying context from the backend.
const buildDisplayLabel = (type, entityId, context) => {
    const ctx = context || {};
    const idSuffix = isValid(entityId) ? ` (#${entityId})` : '';
    if (type === 'Route') {
        if (isValid(ctx.origin) && isValid(ctx.destination)) {
            return `Route: ${ctx.origin} → ${ctx.destination}${idSuffix}`;
        }
        return `Route${idSuffix}`;
    }
    if (type === 'Pickup' || type === 'Drop') {
        const place = isValid(ctx.location) ? ctx.location : (isValid(ctx.city) ? ctx.city : null);
        return isValid(place) ? `${type}: ${place}${idSuffix}` : `${type}${idSuffix}`;
    }
    if (type === 'Schedule') {
        return isValid(ctx.description) ? `Schedule: ${ctx.description}` : `Schedule${idSuffix}`;
    }
    return `${type}${idSuffix}`;
};

// Flattens the {schedules, routes, pickups, drops} response into a single list of change rows.
const buildRows = (logsData) => {
    let rows = [];
    LOG_SOURCES.forEach(({ key, type, idField }) => {
        const logs = logsData[key];
        if (isValidArrayWithValues(logs)) {
            logs.forEach((log, index) => {
                const entityId = log[idField];
                rows.push({
                    key: `${key}_${log.id || entityId || index}_${index}`,
                    type,
                    label: buildDisplayLabel(type, entityId, log.context),
                    date: log.operation_time,
                    agent: log.updated_by,
                    changedFieldKeys: isValidArray(log.changedFieldKeys) ? log.changedFieldKeys : [],
                    rawData: log
                });
            });
        }
    });
    return rows;
};

// Builds a short summary from per-type counts, e.g. "5 Routes, 4 Pickups".
const buildSummary = (counts) =>
    SUMMARY_ORDER
        .filter(type => counts[type])
        .map(type => `${counts[type]} ${type}${counts[type] > 1 ? 's' : ''}`)
        .join(', ');

// Groups the flat change rows by operation (same date + agent) so each user action is one line.
const buildGroups = (rows) => {
    const groupsMap = {};
    rows.forEach(row => {
        const groupKey = `${row.date}__${row.agent}`;
        if (!groupsMap[groupKey]) {
            // NOTE: use "items" (not "children") - Ant Table reserves "children" for tree data.
            groupsMap[groupKey] = { key: groupKey, date: row.date, agent: row.agent, counts: {}, items: [] };
        }
        groupsMap[groupKey].items.push(row);
        groupsMap[groupKey].counts[row.type] = (groupsMap[groupKey].counts[row.type] || 0) + 1;
    });

    const groups = Object.keys(groupsMap).map(groupKey => {
        const group = groupsMap[groupKey];
        group.summary = group.items.length === 1
            ? group.items[0].label
            : `${buildSummary(group.counts)} updated`;
        return group;
    });

    groups.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
    return groups;
};

export const ScheduleLogsTab = (props) => {
    const {
        scheduleId,
        scheduleDescription
    } = props;

    const dispatch = useDispatch();

    const [logsData, setLogsData] = useState({ schedules: [], routes: [], pickups: [], drops: [] });
    const [typeFilter, setTypeFilter] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showModal, setShowModal] = useState(false);

    const setLoading = (bool) => {
        dispatch(loadingAction(bool));
    };

    useEffect(() => {
        if (isValid(scheduleId)) {
            setLoading(true);
            getScheduleUpdateLogsById(scheduleId, (data) => {
                const logs = data.logs || data;
                setLogsData({
                    schedules: isValidArray(logs.schedules) ? logs.schedules : [],
                    routes: isValidArray(logs.routes) ? logs.routes : [],
                    pickups: isValidArray(logs.pickups) ? logs.pickups : [],
                    drops: isValidArray(logs.drops) ? logs.drops : []
                });
                setLoading(false);
            }, (error) => {
                setLoading(false);
                message.error(error || 'Failed to fetch schedule logs');
            });
        }
    }, [scheduleId]);

    const allRows = useMemo(() => buildRows(logsData), [logsData]);

    const groups = useMemo(() => {
        const filteredRows = typeFilter === 'all'
            ? allRows
            : allRows.filter(row => row.type === typeFilter);
        return buildGroups(filteredRows);
    }, [allRows, typeFilter]);

    const typeCounts = useMemo(() => {
        const counts = {};
        allRows.forEach(row => { counts[row.type] = (counts[row.type] || 0) + 1; });
        return counts;
    }, [allRows]);

    const onDetailsClicked = (group) => {
        setSelectedGroup(group);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    // One row per operation (grouped). "Details" opens a single merged popup for the whole operation.
    const groupColumns = [
        {
            title: 'Action',
            dataIndex: 'summary',
            key: 'summary'
        },
        {
            title: 'Date & Time',
            dataIndex: 'date',
            key: 'date',
            render: (text) => formatDateTime(text)
        },
        {
            title: 'Updated By',
            dataIndex: 'agent',
            key: 'agent',
            render: (text) => isValid(text) ? text : 'N/A'
        },
        {
            title: 'Changes',
            dataIndex: '',
            key: 'count',
            render: (text, group) => `${group.items.length} change${group.items.length > 1 ? 's' : ''}`
        },
        {
            title: 'Details',
            dataIndex: '',
            key: 'details',
            render: (text, group) => (
                <a style={{ textDecorationLine: 'underline' }} onClick={() => onDetailsClicked(group)}>
                    Details
                </a>
            )
        }
    ];

    const renderChangedFields = (item) => {
        if (!isValidArrayWithValues(item.changedFieldKeys)) {
            return <Text type="secondary">No field level changes recorded.</Text>;
        }
        return (
            <Descriptions bordered size="small" column={1}>
                {item.changedFieldKeys.map((fieldKey) => {
                    const fieldData = item.rawData ? item.rawData[fieldKey] : null;
                    const rawVal = fieldData && typeof fieldData === 'object' ? fieldData.value : fieldData;
                    const displayVal = TIME_FIELDS.indexOf(fieldKey) !== -1
                        ? formatTimeValue(rawVal)
                        : formatValue(rawVal);
                    return (
                        <Descriptions.Item key={fieldKey} label={formatFieldName(fieldKey)}>
                            <Tag color="green">
                                {displayVal}
                            </Tag>
                        </Descriptions.Item>
                    );
                })}
            </Descriptions>
        );
    };

    return (
        <div className="tab-block">
            <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16, marginBottom: 12 }}>
                <Col>
                    <Text strong>Filter by type: </Text>
                </Col>
                <Col>
                    <Select
                        value={typeFilter}
                        onChange={setTypeFilter}
                        style={{ minWidth: 200 }}
                    >
                        <Option value="all">All ({allRows.length})</Option>
                        {SUMMARY_ORDER.filter(type => typeCounts[type]).map(type => (
                            <Option key={type} value={type}>{`${type} (${typeCounts[type]})`}</Option>
                        ))}
                    </Select>
                </Col>
            </Row>
            <Table
                columns={groupColumns}
                dataSource={groups}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                locale={{ emptyText: 'No schedule level logs found.' }}
            />
            <Modal
                title={'Logs'}
                onOk={handleModalClose}
                onCancel={handleModalClose}
                visible={showModal}
                width={'90vw'}
                footer={null}
                destroyOnClose
            >
                {isValid(selectedGroup) &&
                    <div>
                        <Text style={{ fontWeight: 'bold' }}>{scheduleDescription}</Text>
                        <br />
                        <Text style={{ fontWeight: 'bold' }}>
                            {formatDateTime(selectedGroup.date)} · {isValid(selectedGroup.agent) ? selectedGroup.agent : 'N/A'}
                        </Text>
                        <Divider />
                        {selectedGroup.items.map((item) => (
                            <div key={item.key} style={{ marginBottom: '16px' }}>
                                <Text style={{ fontWeight: 'bold', textDecorationLine: 'underline' }}>
                                    {item.label}
                                </Text>
                                <div style={{ marginTop: '8px' }}>
                                    {renderChangedFields(item)}
                                </div>
                            </div>
                        ))}
                    </div>
                }
            </Modal>
        </div>
    );
};

export default ScheduleLogsTab;
